import { indexBy, prop, path, dissoc, over, lensPath, reject, propEq, isEmpty, dissocPath, defaultTo, keys, identity, find, not, isNil, both, complement } from 'ramda';
import { fetchLocks } from '@rushplay/api-client';
import { createSelector } from 'reselect';
import { SESSION_EXPIRED } from '@rushplay/websockets';

/**
 * @constant
 * @private
 */
var INIT = 'compliance/locks/INIT';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var REMOVED = 'compliance/locks/REMOVED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var CLEARED = 'compliance/locks/CLEARED';
var MESSAGE_DISMISSED = '/compliance/locks/MESSAGE_DISMISSED';

/**
 * Dismiss free text messages by id
 * @memberof rushplay/compliance/locks
 * @param {number} id
 * @returns {Object} Redux action
 */

function dissmissMessage(id) {
  return {
    type: MESSAGE_DISMISSED,
    payload: id
  };
}
/**
 * Write data to state
 * @private
 * @param {Object} locks
 * @returns {Object} Redux action
 */

function init(locks) {
  return {
    type: INIT,
    error: locks instanceof Error,
    payload: locks
  };
}
/**
 * Remove lock
 * @memberof rushplay/compliance/locks
 * @param {string} lockType
 * @returns {Object} Redux action
 */


function remove(lockType) {
  return {
    type: REMOVED,
    payload: lockType
  };
}
/**
 * Fetche and store lock in state
 * @memberof rushplay/compliance/locks
 * @param {Object} config
 * @param {string} [config.token]
 * @returns {Object} Redux action
 */

function fetch(config) {
  return fetchLocks({
    failure: function failure() {
      return init(new Error('locks.errors.failed-loading'));
    },
    success: function success(res) {
      return init(indexBy(prop('type'), res.value));
    },
    token: path(['token'], config),
    version: 1
  });
}
/**
 * Clears the locks state
 * @memberof rushplay/compliance/locks
 * @returns {Object} Redux action
 */

function clear() {
  return {
    type: CLEARED
  };
}

var LOCKS_FALLBACK = {};
/**
 * Get current lock types
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @returns {Array.<string>}
 */

var getLocks = createSelector([defaultTo(LOCKS_FALLBACK)], function (locks) {
  return keys(locks);
});
/**
 * Get lock by type
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.type
 * @returns {?Object}
 */

var getLock = createSelector([identity, function (_, props) {
  return props.type;
}], function (locks, type) {
  return path([type], locks);
});
/**
 * Get `unread-messages` lock messages
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @returns {Array.<Object>}
 */

var getMessages = createSelector(function (state) {
  return getLock(state, {
    type: 'unread-messages'
  });
}, function (lock) {
  return path(['options', 'messages'], lock);
});
/**
 * Get message by id
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.id
 * @returns {?Object}
 */

var getMessage = createSelector([getMessages, function (state, props) {
  return props.id;
}], function (messages, id) {
  return find(propEq('id', id), messages);
});
/**
 * Get initialized state
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @returns {Bool}
 */

function isInitialized(state) {
  return not(isNil(state));
}
/**
 * Get if user have locks
 * @memberof rushplay/compliance/locks
 * @param {Object} state
 * @returns {Bool}
 */

function hasLocks(state) {
  return both(isInitialized, complement(isEmpty))(state);
}

var INITIAL_STATE = null;
function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : INITIAL_STATE;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (action && action.error) {
    return state;
  }

  switch (action && action.type) {
    case INIT:
      {
        return action.payload;
      }

    case REMOVED:
      {
        return dissoc(action.payload, state);
      }

    case MESSAGE_DISMISSED:
      {
        var lock = getLock(state, {
          type: 'unread-messages'
        });

        if (lock) {
          var nextState = over(lensPath([lock.type, 'options', 'messages']), function (messages) {
            return reject(propEq('id', action.payload), messages || []);
          }, state);
          var hasNoMessages = isEmpty(getMessages(nextState));

          if (hasNoMessages) {
            return dissocPath([lock.type], state);
          }

          return nextState;
        }

        return state;
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return INITIAL_STATE;
      }

    default:
      {
        return state;
      }
  }
}

/** @module rushplay/compliance/locks */

export { dissmissMessage, remove, fetch, clear, reducer, getLocks, getLock, getMessages, getMessage, isInitialized, hasLocks, INIT, REMOVED, CLEARED, MESSAGE_DISMISSED };
